.sn-overview-type-2 {
  color: var(--text-color-primary);
  padding-bottom: 60px;

  @include desktop {
    padding-bottom: 80px;
  }

  &__header {
    text-align: center;
    margin-bottom: var(--spacing-3xl);

    .me-heading {
      margin-bottom: var(--spacing-sm);
    }
  }

  // ── Block ────────────────────────────────────────────────────────────────────

  &__block {
    & + & {
      margin-top: var(--spacing-4xl);

      @include desktop {
        margin-top: var(--spacing-5xl);
      }
    }
  }

  // ── Logo ─────────────────────────────────────────────────────────────────────

  // `min-height` matches the logo's `max-height` so the row holds its place while the lazy-loaded
  // image is still in flight. Without it the logos arrive mid-scroll and push the stats below them
  // down; the loaded state is unchanged, since the image never exceeds 40px.
  &__logo {
    text-align: center;
    margin-bottom: var(--spacing-3xl);
    min-height: 40px;
  }

  &__logo-img {
    max-height: 40px;
    width: auto;
  }

  &__logo-text {
    display: inline-block;
    font-size: map-get($font-sizes, 3xl);
    font-weight: map-get($font-weights, bold);
    color: var(--text-color-primary);
    line-height: map-get($line-heights, 2xl);
  }

  // ── Stats cards ──────────────────────────────────────────────────────────────

  &__stats {
    display: flex;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-3xl);
    scrollbar-width: none;
    padding-top: 25px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__stat {
    flex: 1 0 0;
    min-width: 160px;
    text-align: center;
    min-height: 136px;

    @include desktop {
      min-height: 152px;
    }
  }

  &__stat-inner {
    position: relative;
    height: 100%;
    background: var(--background-color-secondary);
    border-radius: 12px;
    padding: var(--spacing-lg) var(--spacing-xl) var(--spacing-md);
  }

  &__stat-icon {
    display: flex;
    justify-content: center;
    margin-bottom: var(--spacing-sm);
    position: absolute;
    top: 0;
    left: 50%;
    translate: -50% -50%;

    .competitor {
      svg {
        rect {
          fill: var(--text-color-tertiary, #9e9e9e);
        }
      }
    }

    svg {
      display: block;
    }
  }

  &__stat-title {
    font-size: map-get($font-sizes, xl);
    font-weight: map-get($font-weights, bold);
    color: var(--text-color-primary);
    margin-bottom: var(--spacing-sm);
    padding-top: var(--spacing-sm);
  }

  &__stat-rating {
    display: flex;
    align-items: end;
    justify-content: center;
    font-size: map-get($font-sizes, 2xl);
    color: var(--text-color-primary);

    .me-icon {
      flex-shrink: 0;
      position: relative;
      height: 24px;
      margin-right: 8px;
      width: 24px;

      @include desktop {
        height: 28px;
        margin-right: 12px;
        top: -6px;
        width: 28px;
      }
    }

    .primary {
      fill: #f8bb0c;
    }

    .competitor {
      fill: var(--text-color-tertiary, #9e9e9e);
    }
  }

  &__stat-rating-value {
    font-size: map-get($font-sizes, 4xl);
    font-weight: 900;
    line-height: 1;

    @include desktop {
      font-size: map-get($font-sizes, 6xl);
    }
  }

  &__stat-price {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-xs);
  }

  &__stat-price-amount {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-xs);

    span {
      font-size: map-get($font-sizes, sm);
      line-height: map-get($line-heights, lg);
      text-align: left;
      max-width: 75px;
    }
  }

  &__stat-plan {
    font-size: map-get($font-sizes, sm);
    color: var(--text-color-tertiary);
  }

  // ── Specs row ────────────────────────────────────────────────────────────────

  &__specs {
    display: flex;
    gap: var(--spacing-2xl);
    margin-bottom: var(--spacing-3xl);
  }

  &__specs-col {
    flex: 1 1 50%;
  }

  &__spec {
    font-size: map-get($font-sizes, md);
    color: var(--text-color-primary);
    margin-bottom: var(--spacing-md);
    line-height: map-get($line-heights, md);

    strong {
      font-weight: map-get($font-weights, bold);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  // ── Advantages ───────────────────────────────────────────────────────────────

  &__advantages {
    display: flex;
    gap: var(--spacing-2xl);
  }

  &__advantages-col {
    flex: 1 1 50%;
  }

  &__advantage {
    margin-bottom: var(--spacing-2xl);

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__advantage-title {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-size: map-get($font-sizes, xl);
    font-weight: map-get($font-weights, bold);
    color: var(--text-color-primary);
    margin-bottom: var(--spacing-sm);
  }

  &__advantage-icon {
    flex-shrink: 0;
    display: block;
  }

  &__advantage-desc {
    font-size: map-get($font-sizes, md);
    color: var(--text-color-primary);
    line-height: map-get($line-heights, lg);
    margin: 0;
  }

  // ── Responsive ───────────────────────────────────────────────────────────────

  @include below-desktop {
    &__specs {
      flex-direction: column;
      gap: 0;
    }

    &__advantages {
      flex-direction: column;
    }

    &__stat-inner {
      padding: var(--spacing-md) var(--spacing-sm);
    }

    &__stat-title {
      font-size: map-get($font-sizes, lg);
    }
  }
}
